const normalizeMultiSelect = (obj) => ({
	type: "multiOptions",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	...(obj.dataRetrieval
		? {
			typeOptions: {
				loadOptionsMethod: obj.dataRetrieval.methodName,
				loadOptionsDependsOn: obj.dataRetrieval.identifierDependencies,
			},
		}
		: {}),
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
	...(obj.choices?.length
		? {
			options: obj.choices.map((choice) => ({
				name: choice.label,
				value: choice.value,
				description: choice.explanation,
			})),
		}
		: {}),
});

const normalizeSelect = (obj) => ({
	type: "options",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	hint: obj.fieldMeta.helper,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	...(obj.dataRetrieval || obj.multiInput //Only add type options if something exists across both
		? {
			typeOptions: {
				...(obj.dataRetrieval
					? {
						loadOptionsMethod: obj.dataRetrieval.methodName,
						loadOptionsDependsOn: obj.dataRetrieval.identifierDependencies,
					}
					: {}),
				...(obj.multiInput
					? {
						multipleValues: obj.multiInput ? true : false,
						multipleValueButtonText: obj.multiInput?.label,
					}
					: {}),
			},
		}
		: {}),
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
	...(obj.valueChange
		? {
			valueChangeOptions: {
				changeInputOutputRestrictionsMethod: obj.valueChange.changeInputOutputRestrictionsMethod,
				destroyConnectionsOnChange: obj.valueChange.destroyConnectionsOnChange,
			},
		}
		: {}),
	options: (obj.choices || []).map((choice) => ({
		name: choice.label,
		value: choice.value,
		description: choice.explanation,
	})),
});

const normalizeSwitch = (obj) => ({
	type: "boolean",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	required: obj.fieldMeta.mandatory,
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeText = (obj) => ({
	type: "string",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	hint: obj.fieldMeta.helper,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	typeOptions: {
		...(obj.dataRetrieval
			? {
				loadOptionsMethod: obj.dataRetrieval.methodName,
				loadOptionsDependsOn: obj.dataRetrieval.identifierDependencies,
			}
			: {}),
		...(obj.multiInput
			? {
				multipleValues: obj.multiInput ? true : false,
				multipleValueButtonText: obj.multiInput?.label,
			}
			: {}),
		...(obj.specifics
			? {
				...(obj.specifics?.isSensitiveField ? { password: obj.specifics?.isSensitiveField } : {}),
				...(obj.specifics?.editorType ? { editor: obj.specifics?.editorType } : {}),
				...(obj.specifics?.maxCharacters ? { maxValue: obj.specifics?.maxCharacters } : {}),
				...(obj.specifics?.defaultLines ? { rows: obj.specifics?.defaultLines } : {}),
				...(obj.specifics?.alwaysEditable ? { alwaysOpenEditWindow: obj.specifics?.alwaysEditable } : {}),
			}
			: {}),
	},
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeGroup = (obj) => ({
	type: "collection",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	typeOptions: {
		...(obj.multiInput
			? {
				multipleValues: true,
				multipleValueButtonText: obj.multiInput.label,
			}
			: {}),
	},
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
	options: obj.group ? normalizeSelectArray(obj.group) : [],
});

const normalizeHelper = (obj) => ({
	type: "notice",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.message,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeFixedGroup = (obj) => ({
	type: "fixedCollection",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	typeOptions: {
		...(obj.multiInput
			? {
				multipleValues: true,
				multipleValueButtonText: obj.multiInput.label,
			}
			: {}),
	},
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
	options: obj.fixedGroups.map((each) => ({
		displayName: each.fieldMeta.label,
		name: each.fieldMeta.identifier,
		values: each.groupedFields ? normalizeSelectArray(each.groupedFields) : [],
	})),
});

const normalizeNumeric = (obj) => ({
	type: "number",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	typeOptions: {
		...(obj.specifics
			? {
				...(obj.specifics.minimum ? { minValue: obj.specifics.minimum } : {}),
				...(obj.specifics.maximum ? { maxValue: obj.specifics.maximum } : {}),
				...(obj.specifics.decimalPlaces ? { numberPrecision: obj.specifics.decimalPlaces } : {}),
			}
			: {}),
	},
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeCalendar = (obj) => ({
	type: "dateTime",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	required: obj.fieldMeta.mandatory,
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeJson = (obj) => ({
	type: "json",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	hint: obj.fieldMeta.helper,
	placeholder: obj.fieldMeta.filler,
	required: obj.fieldMeta.mandatory,
	typeOptions: {
		...(obj.specifics
			? {
				...(obj.specifics.ideVisible ? { alwaysOpenEditWindow: obj.specifics.ideVisible } : {}),
				...(obj.specifics.defaultLines ? { rows: obj.specifics.defaultLines } : {}),
				...(obj.specifics.editorType ? { editor: obj.specifics.editorType } : {}),
				...(obj.specifics.ideLanguage ? { editorLanguage: obj.specifics.ideLanguage } : {}),
			}
			: {}),
	},
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeColour = (obj) => ({
	type: "color",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	required: obj.fieldMeta.mandatory,
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeInvisible = (obj) => ({
	type: "hidden",
	fieldUuid: obj.fieldUuid,
	displayName: obj.fieldMeta.label,
	name: obj.fieldMeta.identifier,
	default: obj.fieldMeta.defaultValue,
	description: obj.fieldMeta.explanation,
	...(obj.conditions
		? {
			displayOptions: {
				...(obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf
					? { show: obj.conditions.visibility.visibleWhenIdentifiersEqualAnyOf }
					: {}),
				...(obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf
					? { hide: obj.conditions.visibility.hiddenWhenIdentifiersEqualAnyOf }
					: {}),
			},
		}
		: {}),
});

const normalizeSelectArray = (options) => {
	return options.map((option) => {
		switch (option.fieldType) {
			case "multiSelect":
				return normalizeMultiSelect(option);
			case "select":
				return normalizeSelect(option);
			case "switch":
				return normalizeSwitch(option);
			case "text":
				return normalizeText(option);
			case "group":
				return normalizeGroup(option);
			case "helper":
				return normalizeHelper(option);
			case "fixedGroup":
				return normalizeFixedGroup(option);
			case "numeric":
				return normalizeNumeric(option);
			case "calendar":
				return normalizeCalendar(option);
			case "json":
				return normalizeJson(option);
			case "colour":
				return normalizeColour(option);
			case "invisible":
				return normalizeInvisible(option);
			default:
				return option;
		}
	});
};

// Main Reverse Transformation Function
export const normalize = (obj) => {
	switch (obj.fieldType) {
		case "multiSelect":
			return normalizeMultiSelect(obj);
		case "select":
			return normalizeSelect(obj);
		case "switch":
			return normalizeSwitch(obj);
		case "text":
			return normalizeText(obj);
		case "group":
			return normalizeGroup(obj);
		case "helper":
			return normalizeHelper(obj);
		case "fixedGroup":
			return normalizeFixedGroup(obj);
		case "numeric":
			return normalizeNumeric(obj);
		case "calendar":
			return normalizeCalendar(obj);
		case "json":
			return normalizeJson(obj);
		case "colour":
			return normalizeColour(obj);
		case "invisible":
			return normalizeInvisible(obj);
		default:
			return obj;
	}
};

export const normalizeFields = (fields) => {
	return fields.map(normalize);
};

export const normalizeCredentialFields = (fields) => {
	return fields.map((field) => {
		if (!field.fieldMeta) {
			return field;
		}
		const { identifier, mandatory } = field.fieldMeta;
		const visibilityConditions = field.conditions?.visibility?.visibleWhenIdentifiersEqualAnyOf || {};
		const matchType = field.conditions?.visibility?.matchType || "all";

		return {
			name: identifier,
			required: mandatory,
			matchType,
			...(Object.keys(visibilityConditions).length > 0
				? { displayOptions: { show: { ...visibilityConditions } } }
				: {}),
		};
	});
};
